import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Calendar, ChefHat, Users, Clock, DollarSign, Pause, Play, XCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const statusColors = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-orange-100 text-orange-700',
  approved: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  confirmed: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-700',
};

export default function Bookings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchBookings = useCallback(() => {
    API.get('/my-bookings').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handlePlanStatus = async (planId, newStatus) => {
    setUpdating(planId);
    try {
      await API.put(`/meal-plans/${planId}/status`, { status: newStatus });
      toast.success(`Meal plan ${newStatus}`, {
        description: newStatus === 'paused' ? 'Your meal plan has been paused. Resume anytime.' :
                     newStatus === 'active' ? 'Your meal plan is active again!' :
                     'Your meal plan has been cancelled.',
      });
      fetchBookings();
    } catch {
      toast.error('Failed to update meal plan');
    }
    setUpdating(null);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E05A47] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="fade-in space-y-6" data-testid="bookings-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope]">My Bookings</h1>

      <Tabs defaultValue="meal-plans">
        <TabsList className="bg-[#F7F7F7] rounded-lg p-1">
          <TabsTrigger value="meal-plans" className="rounded-md text-sm font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-meal-plans">
            Meal Plans ({data?.meal_plans?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="in-home" className="rounded-md text-sm font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-in-home">
            In-Home ({data?.in_home_bookings?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="events" className="rounded-md text-sm font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-events">
            Events ({data?.event_bookings?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="meal-plans" className="mt-4 space-y-3">
          {data?.meal_plans?.length === 0 && <EmptyState text="No meal plans yet" />}
          {data?.meal_plans?.map(p => (
            <div key={p.id} className="bg-white rounded-xl border border-[#EBEBEB] p-5" data-testid={`meal-plan-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                    <ChefHat className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-bold text-[#222] font-[Manrope] capitalize">{p.plan_type} Plan</p>
                    <p className="text-xs text-[#717171]">{p.meals_per_day} meals/day · {p.frequency} · {p.duration_weeks} weeks</p>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[p.status] || 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-[#717171]">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />Starts {new Date(p.start_date).toLocaleDateString()}</span>
                <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" strokeWidth={1.5} />${p.total_price} total</span>
                <span className="flex items-center gap-1 text-[#E05A47] font-semibold">Payment: {p.payment_method}</span>
              </div>
              {/* Pause / Resume / Cancel Actions */}
              {p.status !== 'cancelled' && (
                <div className="mt-4 pt-3 border-t border-[#EBEBEB] flex items-center gap-2">
                  {p.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePlanStatus(p.id, 'paused')}
                      disabled={updating === p.id}
                      className="rounded-lg text-xs gap-1.5 border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                      data-testid={`pause-plan-${p.id}`}
                    >
                      <Pause className="w-3.5 h-3.5" /> Pause Plan
                    </Button>
                  )}
                  {p.status === 'paused' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePlanStatus(p.id, 'active')}
                      disabled={updating === p.id}
                      className="rounded-lg text-xs gap-1.5 border-green-300 text-green-700 hover:bg-green-50"
                      data-testid={`resume-plan-${p.id}`}
                    >
                      <Play className="w-3.5 h-3.5" /> Resume Plan
                    </Button>
                  )}
                  {(p.status === 'active' || p.status === 'paused') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to cancel this meal plan? This cannot be undone.')) {
                          handlePlanStatus(p.id, 'cancelled');
                        }
                      }}
                      disabled={updating === p.id}
                      className="rounded-lg text-xs gap-1.5 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`cancel-plan-${p.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> Cancel
                    </Button>
                  )}
                  {updating === p.id && <span className="w-4 h-4 border-2 border-[#E05A47] border-t-transparent rounded-full animate-spin" />}
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="in-home" className="mt-4 space-y-3">
          {data?.in_home_bookings?.length === 0 && <EmptyState text="No in-home bookings yet" />}
          {data?.in_home_bookings?.map(b => (
            <div key={b.id} className="bg-white rounded-xl border border-[#EBEBEB] p-5" data-testid={`in-home-booking-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-bold text-[#222] font-[Manrope] capitalize">{b.booking_type?.replace('-', ' ')}</p>
                    <p className="text-xs text-[#717171]">{b.guests} guests · {b.special_requests || 'No special requests'}</p>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[b.status] || 'bg-gray-100 text-gray-600'}`}>{b.status}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-[#717171]">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} />{new Date(b.date).toLocaleDateString()} at {b.time}</span>
                <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" strokeWidth={1.5} />${b.price}</span>
                <span className="flex items-center gap-1 text-[#E05A47] font-semibold">Payment: {b.payment_method}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="events" className="mt-4 space-y-3">
          {data?.event_bookings?.length === 0 && <EmptyState text="No event bookings yet" />}
          {data?.event_bookings?.map(e => (
            <div key={e.id} className="bg-white rounded-xl border border-[#EBEBEB] p-5" data-testid={`event-booking-${e.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-bold text-[#222] font-[Manrope]">{e.event_title}</p>
                    <p className="text-xs text-[#717171]">{e.seats} seat{e.seats > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[e.status] || 'bg-gray-100 text-gray-600'}`}>{e.status}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-[#717171]">
                <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" strokeWidth={1.5} />${e.total_price}</span>
                <span className="flex items-center gap-1 text-[#E05A47] font-semibold">Payment: {e.payment_method}</span>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-center py-12 text-[#717171]">{text}</div>;
}
